// ============================================================================
// Doctor availability + patient bookings API client.
//
// Backend routes (see backend/bookings/urls.py + core/urls.py):
//   GET    /api/availability/slots/?doctor_id=&date_from=&date_to=&available_only=true
//   POST   /api/availability/slots/                  single slot create (doctor-scoped)
//   POST   /api/availability/slots/bulk_create/      drag-select editor send
//   DELETE /api/availability/slots/{id}/             refuses if active booking
//
//   GET    /api/bookings/                            scoped to caller (patient or doctor)
//   POST   /api/bookings/   {slot_id, reason?}       atomic claim
//   POST   /api/bookings/{id}/cancel/  {reason?}     soft-cancel, frees slot
// ============================================================================

import { api } from './client.js'
import { unwrapList } from '../utils/format.js'

// ---- Doctor availability ---------------------------------------------------

export async function listAvailableSlots({
  doctorId,
  dateFrom,
  dateTo,
  availableOnly = true,
} = {}) {
  const params = {}
  if (doctorId)   params.doctor_id     = doctorId
  if (dateFrom)   params.date_from     = dateFrom
  if (dateTo)     params.date_to       = dateTo
  if (availableOnly) params.available_only = 'true'
  const { data } = await api.get('/api/availability/slots/', { params })
  return unwrapList(data)
}

export async function createSlot({ slotAt, durationMinutes = 15 }) {
  const { data } = await api.post('/api/availability/slots/', {
    slot_at: slotAt,
    duration_minutes: durationMinutes,
  })
  return data
}

/**
 * Bulk-save many slots from the doctor's drag-select editor. Returns
 *   { created_count, skipped_count, created: [slots] }
 * so the FE can flash "Saved 47 slots, 3 already existed" without a
 * second round-trip.
 */
export async function bulkCreateSlots(slots) {
  // Each entry: { slotAt, durationMinutes? }
  const payload = {
    slots: slots.map((s) => ({
      slot_at: s.slotAt,
      duration_minutes: s.durationMinutes || 15,
    })),
  }
  const { data } = await api.post('/api/availability/slots/bulk_create/', payload)
  return data
}

export async function deleteSlot(id) {
  await api.delete(`/api/availability/slots/${id}/`)
}

// ---- Bookings --------------------------------------------------------------

export async function listBookings(params = {}) {
  const { data } = await api.get('/api/bookings/', {
    params: { ordering: '-created_at', page_size: 100, ...params },
  })
  return unwrapList(data)
}

export async function createBooking({ slotId, reason = '' }) {
  const { data } = await api.post('/api/bookings/', {
    slot_id: slotId,
    reason,
  })
  return data
}

export async function cancelBooking(id, reason = '') {
  const { data } = await api.post(`/api/bookings/${id}/cancel/`, { reason })
  return data
}

// ---- Doctor discovery (for the patient's specialty filter) -----------------

/**
 * The patient's "find a doctor" search. Re-uses the existing
 * /api/doctors/ list endpoint with a specialty query param. The
 * DoctorProfileViewSet already declares filterset_fields=["user_type",
 * "specialty"] so server-side filtering works out of the box.
 */
export async function listDoctors({ specialty = '', userType = '', search = '' } = {}) {
  const params = {}
  if (specialty) params.specialty = specialty
  if (userType)  params.user_type = userType
  if (search)    params.search    = search
  const { data } = await api.get('/api/doctors/', { params })
  return unwrapList(data)
}
